import React, { useEffect, useRef, useState } from 'react';
import { Input, type Props as InputProps } from './Input';
import { Box, InputLabel, Stack, SxProps, Typography } from '@mui/material';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { EditableTextInputActions } from './EditableTextInputActions';
import { Error } from '@mui/icons-material';

type Props = InputProps & {
  onSave?: (value: string) => void;
  startAdornment?: React.ReactNode | null;
  isDeletable?: boolean;
  onDelete?: () => void;
  onEditModeChange?: (isEditing: boolean) => void;
  hideDisplayText?: boolean;
  checkboxLabel?: string;
  showCheckbox?: boolean;
  checkboxValue?: boolean;
  onCheckboxChange?: (value: boolean) => void;
  disableEdit?: boolean;
  inputContainer?: SxProps;
  displayTextProps?: SxProps;
  boxContainer?: SxProps;
  errorMessage?: string;
  errorMessageSx?: SxProps;
  showEndOfInputActions?: boolean;
  bottomActions?: {
    showSave?: boolean;
    showCancel?: boolean;
    showDelete?: boolean;
    showEdit?: boolean;
    useIcons?: boolean;
    customActions?: React.ReactNode;
  };
};

const headerContainer: SxProps = {
  display: 'flex',
  alignContent: 'center',
  gap: 2,
  mb: 2
};

const labelStyle: SxProps = {
  alignSelf: 'center',
  m: 0
};

const checkBoxStyle: SxProps = {
  pl: 0
};

const checkBoxContainer: SxProps = {
  mt: 1
};

const inputStyle: SxProps = {
  backgroundColor: 'white'
};

const actionsContainer: SxProps = {
  mt: 1
};

const DisplayText: React.FC<{
  text: string;
  textProps?: SxProps;
  startAdornment?: React.ReactNode | null;
  hideDisplayText?: boolean;
}> = ({ text, hideDisplayText, startAdornment, textProps }) => {
  if (hideDisplayText) {
    return null;
  }

  return (
    <Box display="flex" width="100%" alignItems="center">
      {startAdornment && <>{startAdornment}</>}
      <Typography sx={textProps} variant="summary">
        {text}
      </Typography>
    </Box>
  );
};

export const EditableTextInput: React.FC<Props> = ({
  onSave,
  startAdornment,
  isDeletable,
  onDelete,
  onEditModeChange,
  hideDisplayText,
  checkboxLabel,
  showCheckbox,
  checkboxValue,
  onCheckboxChange,
  disableEdit,
  inputContainer,
  displayTextProps,
  boxContainer,
  errorMessage,
  errorMessageSx,
  showEndOfInputActions,
  bottomActions,
  ...props
}) => {
  const { label, value, onChange, ...inputProps } = props;
  const [savedValue, setSavedValue] = useState(String(value));
  const [text, setText] = useState('');
  const [error, setError] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setText(String(value));
  }, [value]);

  const isInEditModeInitially = value ? false : true;
  const [isEditing, setIsEditing] = useState(isInEditModeInitially);

  useEffect(() => {
    if (isEditing) {
      inputRef?.current?.focus();
    }
  }, [isEditing, inputRef]);

  useEffect(() => {
    if (errorMessage === undefined) {
      return;
    }
    const message =
      errorMessage && props.error && !text ? errorMessage : undefined;

    setError(message);
  }, [errorMessage, text, props.error]);

  const handleSave = () => {
    if (!error && !text && errorMessage) {
      const message = errorMessage && !text ? errorMessage : undefined;

      setError(message);
      return;
    }

    if (error) {
      return;
    }

    onSave?.(text);
    setIsEditing(false);
    setSavedValue(text);
  };

  const handleCancel = () => {
    if (!savedValue && onDelete) {
      onDelete();
      return;
    }

    setIsEditing(false);
    setText(savedValue);
    onSave?.(savedValue);
    onChange?.({
      target: {
        name: inputProps.name,
        id: inputProps.id,
        value: savedValue
      } as HTMLInputElement
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleDelete = () => {
    onDelete?.();
    setIsEditing(false);
  };

  const onActionClick = () => {
    if (isEditing) {
      if (!text) {
        const message =
          errorMessage || 'Add a question, or delete this field to continue';
        setError(message);
        return;
      }
      handleSave();
    } else {
      setIsEditing(true);
    }

    onEditModeChange?.(!isEditing);
  };

  const renderBottomActions = () => {
    if (bottomActions) {
      return (
        <Box sx={actionsContainer}>
          <EditableTextInputActions
            onActionClick={onActionClick}
            isEditing={isEditing}
            isDeletable={isDeletable}
            onDelete={handleDelete}
            disableEdit={disableEdit}
            useIcons={bottomActions.useIcons}
            onEdit={() => setIsEditing(true)}
            onSave={handleSave}
            onCancel={handleCancel}
            bottomActions={bottomActions}
          />
        </Box>
      );
    }
  };

  return (
    <Box sx={boxContainer}>
      {label && (
        <Box sx={headerContainer}>
          <InputLabel sx={labelStyle}>{label}</InputLabel>
          <EditableTextInputActions
            onActionClick={onActionClick}
            isEditing={isEditing}
            isDeletable={isDeletable}
            onDelete={handleDelete}
            disableEdit={disableEdit}
            useIcons={false}
            onEdit={() => setIsEditing(true)}
            onSave={onActionClick}
            onCancel={handleCancel}
            bottomActions={{
              showSave: true,
              showDelete: isDeletable,
              showEdit: true
            }}
          />
        </Box>
      )}

      {isEditing ? (
        <Box sx={inputContainer}>
          {!!error && (
            <Stack direction="row" gap="5px" sx={{ mt: 0.5 }}>
              <Error
                color="error"
                sx={{
                  mt: '2px',
                  fontSize: '16px'
                }}
              />
              <Typography
                color="error"
                variant="body2"
                sx={{
                  fontSize: '14px',
                  ...errorMessageSx
                }}
              >
                {error}
              </Typography>
            </Stack>
          )}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="30px"
          >
            <Input
              {...inputProps}
              inputRef={inputRef}
              value={text}
              onChange={(e) => {
                setError(undefined);
                setText(e.target.value);
                onChange?.(e);
              }}
              sx={inputStyle}
              error={!!error}
            />
            {!!showEndOfInputActions && (
              <EditableTextInputActions
                onActionClick={onActionClick}
                isEditing={isEditing}
                isDeletable={isDeletable}
                onDelete={handleDelete}
                disableEdit={disableEdit}
                useIcons={true}
                onEdit={() => setIsEditing(true)}
                onSave={onActionClick}
                onCancel={handleCancel}
                bottomActions={{
                  showSave: true,
                  showDelete: isDeletable,
                  showEdit: true
                }}
              />
            )}
          </Stack>
        </Box>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap="30px"
        >
          <DisplayText
            text={text}
            textProps={displayTextProps}
            startAdornment={startAdornment}
            hideDisplayText={hideDisplayText}
          />
          {!!showEndOfInputActions && (
            <EditableTextInputActions
              onActionClick={onActionClick}
              isEditing={isEditing}
              isDeletable={isDeletable}
              onDelete={handleDelete}
              disableEdit={disableEdit}
              useIcons={true}
              onEdit={() => setIsEditing(true)}
              onSave={onActionClick}
              onCancel={handleCancel}
              bottomActions={{
                showSave: true,
                showDelete: isDeletable,
                showEdit: true
              }}
            />
          )}
        </Stack>
      )}
      {renderBottomActions()}
      {showCheckbox && (
        <Box sx={checkBoxContainer}>
          <Checkbox
            label={checkboxLabel}
            checked={checkboxValue}
            onChange={(e) => {
              onCheckboxChange?.(e.target.checked);
            }}
            sx={checkBoxStyle}
          />
        </Box>
      )}
    </Box>
  );
};
